import type { LanguageUrl } from '@/types/general';

import { openAPIClient } from '../openApiClient';
import type {
  CourseByIdResponse,
  CourseListResponse,
  CourseListServiceConfig,
  CreateCourseResponse,
  CreateCourseServiceConfig,
  DeleteCourseServiceConfig,
  GetCourseByIdServiceConfig,
  GetCourseExportResponse,
  GetCourseExportServiceConfig,
  GetCourseHandoutResponse,
  GetCourseHandoutServiceConfig,
  LinkableCoursesResponse,
  LinkableCoursesServiceConfig,
  LinkUnitToCourseServiceConfig,
  ReorderCoursesServiceConfig,
  UnlinkUnitFromCourseServiceConfig,
  UpdateCourseResponse,
  UpdateCourseServiceConfig,
  ValidateUnitResponse,
  ValidateUnitServiceConfig,
} from './types';

/**
 * Gets a paginated list of courses
 * Used in: CourseTable for displaying the list of courses
 */
export async function getCourseListService({
  httpRequestConfig,
  params,
}: CourseListServiceConfig): Promise<CourseListResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/course/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
      query: {
        ...params.query,
        // because of CourseListQueryParams type, we need to cast the filters to a string while it's an object in reality and rely on the openapi client to convert it to a string
        filters: params.query.filters as string,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Gets a course by ID
 * Used for course detail views
 */
export async function getCourseByIdService({
  httpRequestConfig,
  params,
}: GetCourseByIdServiceConfig): Promise<CourseByIdResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/course/{spaceId}/{id}/{courseLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        courseLanguage: params.params.courseLanguage,
      },
      query: params.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Creates a new course with language-specific URL support
 * Used in course creation forms
 */
export async function createCourseService({
  httpRequestConfig,
  params,
}: CreateCourseServiceConfig): Promise<CreateCourseResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/course/{spaceId}/{courseLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        courseLanguage: params.params.courseLanguage,
      },
    },
    body: {
      title: params.body.title, // API expects 'title' not 'courseTitle'
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  if (response.data === undefined) {
    throw new Error('No data returned from create course');
  }

  return response.data;
}

/**
 * Updates an existing course with language-specific URL support
 * Used in course editing forms
 */
export async function updateCourseService({
  httpRequestConfig,
  params,
}: UpdateCourseServiceConfig): Promise<UpdateCourseResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  // Transform language URLs for API compatibility
  const requestBody = {
    ...params.body,
    ...(params.body.videoUrls && { videoUrls: params.body.videoUrls }),
    ...(params.body.surveyUrls && { surveyUrls: params.body.surveyUrls }),
  };

  const response = await client.PUT('/v1/auth/course/{spaceId}/{id}/{courseLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        courseLanguage: params.params.courseLanguage,
      },
      query: params.query,
    },
    body: requestBody,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Deletes a course
 * Used in course deletion confirmations
 */
export async function deleteCourseService({
  httpRequestConfig,
  params,
}: DeleteCourseServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.DELETE('/v1/auth/course/{spaceId}/{id}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}

/**
 * Gets a list of linkable courses for a quiz
 * Used for searching courses to link to quizzes
 */
export async function getLinkableCoursesService({
  httpRequestConfig,
  params,
}: LinkableCoursesServiceConfig): Promise<LinkableCoursesResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/courseActions/linkableCourses/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
      query: params.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Links a unit (quiz/sequence) to a course
 * Used when linking units to courses
 */
export async function linkUnitToCourseService({
  httpRequestConfig,
  params,
}: LinkUnitToCourseServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PUT('/v1/auth/courseActions/linkUnitToCourse/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}

/**
 * Unlinks a unit (quiz/sequence) from a course
 * Used when unlinking units from courses
 */
export async function unlinkUnitFromCourseService({
  httpRequestConfig,
  params,
}: UnlinkUnitFromCourseServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PUT('/v1/auth/courseActions/unlinkUnitFromCourse/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}

/**
 * Reorders courses by position
 * Used for drag and drop reordering in course tables
 */
export async function reorderCoursesService({
  httpRequestConfig,
  params,
}: ReorderCoursesServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PUT('/v1/auth/courseActions/reorderCourses/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}

/**
 * Validates a unit (quiz/sequence) for course requirements
 * Used to check if a unit meets all requirements for being part of a course
 */
export async function validateUnitService({
  httpRequestConfig,
  params,
}: ValidateUnitServiceConfig): Promise<ValidateUnitResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/courseActions/validateUnit/{spaceId}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Publishes a course and all its units
 * Uses the updateCourse endpoint with PUBLISH_ALL_UNITS additionalAction
 * Used when publishing an entire course
 */
export async function publishCourseService({
  httpRequestConfig,
  params,
}: UpdateCourseServiceConfig): Promise<UpdateCourseResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  // Ensure we have the PUBLISH_ALL_UNITS action in the query
  const response = await client.PUT('/v1/auth/course/{spaceId}/{id}/{courseLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        courseLanguage: params.params.courseLanguage,
      },
      query: {
        ...params.query,
        additionalAction: 'PUBLISH_ALL_UNITS',
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Validates a course structure and all its units
 * Checks if the course meets all requirements for publication
 * Used in course validation workflows
 */
export async function validateCourseService({
  httpRequestConfig,
  params,
}: GetCourseByIdServiceConfig): Promise<CourseByIdResponse> {
  // For course validation, we fetch the course with validation data
  // The API returns validation information in the response
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/course/{spaceId}/{id}/{courseLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        courseLanguage: params.params.courseLanguage,
      },
      query: params.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Gets course handout download URL
 * Used for generating course handout documents
 */
export async function getCourseHandoutService({
  httpRequestConfig,
  params,
}: GetCourseHandoutServiceConfig): Promise<GetCourseHandoutResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/courseActions/getCourseHandout/{spaceId}/{id}/{contentLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        contentLanguage: params.params.contentLanguage,
      },
      query: {
        language: params.query.language,
      },
    },
  });

  if (response.error) {
    throw new Error(response.error.message || 'Failed to get course handout');
  }

  return response.data;
}

/**
 * Gets course export download URL
 * Used for exporting course content as an XLSX file
 */
export async function getCourseExportService({
  httpRequestConfig,
  params,
}: GetCourseExportServiceConfig): Promise<GetCourseExportResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/courseActions/getCourseExport/{spaceId}/{id}/{contentLanguage}', {
    params: {
      path: {
        spaceId: httpRequestConfig.space,
        id: params.params.id,
        contentLanguage: params.params.contentLanguage,
      },
      query: {
        language: params.query.language,
      },
    },
  });

  if (response.error) {
    throw new Error(response.error.message || 'Failed to get course export');
  }

  return response.data;
}

/**
 * Utility function to convert course query params to handout query params
 * Handles the courseLanguage -> contentLanguage mapping
 */
export function convertCourseParamsToHandoutParams(
  courseParams: GetCourseByIdServiceConfig,
): GetCourseHandoutServiceConfig | null {
  if (!courseParams.params?.params) {
    return null;
  }

  return {
    httpRequestConfig: courseParams.httpRequestConfig,
    params: {
      params: {
        spaceId: courseParams.params.params.spaceId,
        id: courseParams.params.params.id,
        contentLanguage: courseParams.params.params.courseLanguage, // Map courseLanguage to contentLanguage
      },
      query: courseParams.params.query,
    },
  };
}

/**
 * Utility function to transform language URL data for API requests
 * Ensures proper format and validation of language-specific URLs
 */
export function transformLanguageUrlsForAPI(
  videoUrls: LanguageUrl[] = [],
  surveyUrls: LanguageUrl[] = [],
) {
  return {
    videoUrls: videoUrls.filter(item => item.languageCode && item.url !== null),
    surveyUrls: surveyUrls.filter(item => item.languageCode && item.url !== null),
  };
}

/**
 * Utility function to validate language URL structure
 * Ensures data integrity before API submission
 */
export function validateLanguageUrls(
  languageUrls: LanguageUrl[],
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const item of languageUrls) {
    if (!item.languageCode || item.languageCode.trim() === '') {
      errors.push('Language code is required');
    }

    if (item.url !== null && item.url !== '') {
      try {
        // eslint-disable-next-line no-new
        new URL(item.url);
      } catch {
        errors.push(`Invalid URL format for language ${item.languageCode}: ${item.url}`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

// Re-export enhanced handout service
